import { AddVerticalFades } from '#app/components/AddVerticalFades/AddVerticalFades.js';
import { MountedPortal } from '#app/components/MountedPortal/MountedPortal.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { styledComponentsTheme } from '#app/theme/styledComponentsTheme.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import { DialogSelectInputGroup } from './DialogSelectInputGroup.js';
import { DialogSelectValidationItem } from './DialogSelectValidationItem.js';
import { type UseFloatingReturn } from '@floating-ui/react-dom-interactions';
import { usePreventScroll } from '@react-aria/overlays';
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from 'react';
import { css, keyframes, styled } from 'styled-components';

const animateIn = keyframes`
from {
  opacity: 0;
}

to {
  opacity: 1;
}
`;

type ContainerProps = {
  $desktopWidth?: string;
  $strategy: UseFloatingReturn['strategy'];
  $x: UseFloatingReturn['x'];
  $y: UseFloatingReturn['y'];
};

const Container = styled.div<ContainerProps>`
  ${({ $desktopWidth = '320px', $strategy, $x, $y, theme }) => css`
    display: flex;
    flex-direction: column;
    box-shadow: ${theme.boxShadow.basic20};
    background-color: white;
    z-index: ${theme.zIndices.bottomDrawer.dialog};
    animation: ${animateIn} 300ms ease forwards;
    width: 100%;
    max-width: 100vw;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    ${theme.mediaQueries.sm} {
      border-radius: 8px;
      z-index: ${theme.zIndices.menu};
      position: ${$strategy};
      top: ${$y ?? 0}px;
      left: ${$x ?? 0}px;
      right: unset;
      bottom: unset;
      height: unset;
      width: ${$desktopWidth};
    }
  `}
`;

type DialogSelectContainerProps = ComponentPropsWithoutRef<typeof Container> & {
  /**
   * You should skip the portal if you are rendering the dialog select in a modal. This is because the
   * portal will render below the modal in the DOM and cause issues interacting with input fields within
   * on mobile.
   */
  readonly $skipPortal?: boolean;
};

const DialogSelectContainer = forwardRef(
  (
    {
      $skipPortal = false,
      $strategy,
      $x,
      $y,
      ...props
    }: DialogSelectContainerProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const isTablet = useMatchMedia(
      parseMediaQueryForMatchMedia(styledComponentsTheme.mediaQueries.sm),
    );
    usePreventScroll({ isDisabled: isTablet });

    return isTablet || $skipPortal ? (
      <Container
        $strategy={$strategy}
        $x={$x}
        $y={$y}
        ref={ref}
        {...props}
      />
    ) : (
      <MountedPortal>
        <Container
          $strategy={$strategy}
          $x={$x}
          $y={$y}
          ref={ref}
          {...props}
        />
      </MountedPortal>
    );
  },
);

const ListContainer = styled.div<{ $maxHeight?: number }>`
  ${({ $maxHeight = 400, theme }) => css`
    max-height: 100%;
    overflow-y: auto;
    position: relative;

    ${theme.mediaQueries.sm} {
      max-height: ${$maxHeight}px;
    }
  `}
`;

const List = styled.ul`
  & > :last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const GroupListItem = styled.li<{
  $isDefaultGroup?: boolean;
  $isFirstGroup: boolean;
}>`
  ${({ $isDefaultGroup = false, $isFirstGroup, theme }) => css`
    padding: 8px 12px;
    font-size: 12px;
    line-height: 1.2;
    letter-spacing: 0.15px;
    color: ${theme.colorsV2.gray70};

    ${!$isFirstGroup &&
    css`
      border-top: 1px solid ${theme.colorsV2.gray30};
    `}

    ${$isDefaultGroup &&
    css`
      padding: unset;
      line-height: unset;
    `}
  `}
`;

const Input = styled.input`
  ${({ theme }) => css`
    border-radius: 8px;
    padding: 12px 16px 12px 16px;
    font-size: 16px;
    line-height: 1.5;
    width: 100%;
    border: 1px solid ${theme.colorsV2.gray30};

    &::placeholder {
      color: ${theme.colorsV2.gray60};
    }

    &:focus {
      border: 1px solid ${theme.colorsV2.gray30};
    }

    ${theme.mediaQueries.sm} {
      padding: 12px 16px;
      border: unset;
      border-radius: 8px 8px 0 0;
      border-bottom: 1px solid ${theme.colorsV2.black};

      &:focus {
        border: unset;
        border-bottom: 1px solid ${theme.colorsV2.black};
      }
    }
  `}
`;

const InputLoader = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const ListItem = styled.li<{ isHighlighted: boolean }>`
  ${({ isHighlighted, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px 12px 16px;
    cursor: pointer;
    line-height: 1.5;

    ${isHighlighted &&
    css`
      background-color: ${theme.colorsV2.gray05};
    `};
  `}
`;

const CreateItem = styled(ListItem)`
  ${({ theme }) => css`
    & > span {
      color: ${theme.colorsV2.gray70};
    }
    justify-content: flex-start;
    gap: 8px;
  `}
`;

const ListItemLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ListItemIcon = styled.div<{ $size?: number }>`
  ${({ $size = 24 }) => css`
    display: flex;
    align-items: center;
    margin-right: 12px;
    width: ${$size}px;
    border-radius: 50%;
    overflow: hidden;
    height: ${$size}px;
  `}
`;

const CreateItemIcon = styled(ListItemIcon)`
  justify-content: center;
  margin-right: 0;
  height: 32px;
  width: 32px;
  border: 1px solid ${({ theme }) => theme.colorsV2.gray40};
`;

const EmptyState = styled.div`
  ${({ theme }) => css`
    padding: 12px 16px;
    font-size: 12px;
    line-height: 1.33;
    letter-spacing: 0.15px;
    color: ${theme.colorsV2.gray70};
    width: 100%;
  `}
`;

const VerticalFades = styled(AddVerticalFades)`
  overflow: hidden;
`;

export const DialogSelect = Object.assign(DialogSelectContainer, {
  CreateItem,
  CreateItemIcon,
  EmptyState,
  GroupListItem,
  Input,
  InputGroup: DialogSelectInputGroup,
  InputLoader,
  List,
  ListContainer,
  ListItem,
  ListItemIcon,
  ListItemLabel,
  ValidationItem: DialogSelectValidationItem,
  VerticalFades,
});
